"use client";

import { RecommendedProducts } from "@/features/recommendation";
import { Link } from "@/shared";
import { ViewportTracking } from "@dafiti/analytics-sdk";
import type { ViewportTrackingData } from "@dafiti/analytics-sdk";
import {
  Breadcrumb,
  BreadcrumbAction,
  BreadcrumbItem,
  Divider,
} from "@dafiti/closet-ui";
import { cn } from "@dafiti/closet-ui/utils";
import { useTranslations } from "next-intl";

import { WishlistProduct } from "../../api/wishlist.query";
import { useWishlistResults } from "../../hooks/useWishlistResults.hook";
import * as track from "../../tracking/wishlist.tracking";
import { WISHLIST_RECOMMENDED_PRODUCTS_LIST_NAME } from "../../tracking/wishlist.tracking";
import { WishlistPaginationBar } from "../WishlistPaginationBar/WishlistPaginationBar";
import { WishlistProductCard } from "../WishlistProductCard/WishlistProductCard";
import { Init } from "./Init";
import { WishlistLoadMore } from "./WishlistLoadMore";

const gridClasses = cn(
  "grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2 lg:gap-6 gap-y-8",
);

function WishlistGrid({ products }: { products: WishlistProduct[] }) {
  const handleViewTracking = (
    trackingData: ViewportTrackingData<WishlistProduct & { index: number }>[],
  ) => {
    track.viewWishlistItemList(trackingData.map((t) => t.data!));
  };

  return (
    <ViewportTracking onTrack={handleViewTracking}>
      <div className={gridClasses} data-testid="wishlist-grid">
        {products.map((product, i) => (
          <WishlistProductCard
            key={product.sku}
            product={product}
            index={i + 1}
          />
        ))}
      </div>
    </ViewportTracking>
  );
}

type Props = {
  page: number;
  userAgent?: string | null;
};

export function WishlistPage({ page, userAgent }: Props) {
  const t = useTranslations("wishlist");
  const {
    products,
    isLoading,
    isLastPage,
    totalPages,
    loadMoreProducts,
    isFetchingMore,
  } = useWishlistResults({ page, userAgent });

  const isEmpty = !isLoading && products.length === 0;

  const firstProduct = products[0];

  return (
    <div>
      <Init />

      <div className="mb-2">
        <Breadcrumb data-testid="wishlist-breadcrumb">
          <BreadcrumbItem>
            <BreadcrumbAction as={Link} href="/" useRouterLink>
              {t("breadcrumb.home")}
            </BreadcrumbAction>
          </BreadcrumbItem>
          <BreadcrumbItem isLast>
            <BreadcrumbAction as={Link} href="/meus-favoritos" useRouterLink>
              {t("breadcrumb.current")}
            </BreadcrumbAction>
          </BreadcrumbItem>
        </Breadcrumb>
      </div>

      <h1 className="text-heading-3 font-bold mb-4 lg:mb-6">
        {t("page.title")}
      </h1>

      {isLoading && (
        <div className={cn(gridClasses, "opacity-60")}>
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="animate-pulse bg-gray-100 rounded-lg aspect-[131/190]"
            />
          ))}
        </div>
      )}

      {isEmpty && (
        <p className="text-center text-gray-500 py-16">{t("page.empty")}</p>
      )}

      {!isLoading && products.length > 0 && (
        <>
          <WishlistGrid products={products} />

          <WishlistLoadMore
            isLastPage={isLastPage}
            isFetchingMore={isFetchingMore}
            onLoadMore={loadMoreProducts}
          />

          {totalPages > 1 && (
            <div className="hidden lg:block" data-testid="wishlist-pagination">
              <WishlistPaginationBar
                totalPages={totalPages}
                currentPage={page}
              />
            </div>
          )}
        </>
      )}

      <div className="mt-8">
        <RecommendedProducts
          product={firstProduct}
          title={t("page.recommendedTitle")}
          trackingListName={WISHLIST_RECOMMENDED_PRODUCTS_LIST_NAME}
          variant="carousel"
        />

        <Divider className="bg-gray-200 mb-2 lg:hidden mt-8 lg:mt-0" />
      </div>
    </div>
  );
}
